const DEFAULT_PREPROD_API = "https://api.preprod.samai.co.in";

declare global {
  interface Window {
    __API_BASE_URL__?: string;
    __API_WS_URL__?: string;
  }
}

const getRawBaseUrl = (): string => {
  if (typeof window !== "undefined" && window.__API_BASE_URL__) {
    return window.__API_BASE_URL__.trim().replace(/\/+$/, "");
  }
  return (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "";
};

const getRawSocketUrl = (): string =>
  (typeof process !== "undefined" && process.env?.REACT_APP_SOCKET_URL) || "";

function isPreprodHost(): boolean {
  if (typeof window !== "undefined" && window.location?.hostname) {
    return window.location.hostname === "preprod.samai.co.in";
  }
  return false;
}

export function getApiBaseUrl(): string {
  if (isPreprodHost()) {
    return DEFAULT_PREPROD_API;
  }
  let raw = getRawBaseUrl().trim().replace(/\/+$/, "");
  if (raw === "undefined" || raw === "null") raw = "";
  if (!raw && process.env.NODE_ENV !== "production") {
    raw = DEFAULT_PREPROD_API;
  }
  if (process.env.NODE_ENV === "production" && !raw) {
    console.error(
      "[config] REACT_APP_API_URL is required in production. Set it in your environment or .env.production."
    );
  }
  return raw;
}

export function getSocketUrl(): string {
  const explicit = getRawSocketUrl().trim();
  if (explicit) return explicit;

  const base = getApiBaseUrl();
  if (base) {
    const wsBase = base.replace(/^http/, "ws");
    return wsBase.endsWith("/") ? `${wsBase}ws` : `${wsBase}/ws`;
  }

  if (typeof window !== "undefined" && window.__API_WS_URL__) {
    return window.__API_WS_URL__.trim();
  }
  return "";
}

export function apiPath(path: string): string {
  const base = getApiBaseUrl();
  if (!base) return "";
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalized}`;
}

export const API_PATHS = {
  GET_CASES_URL: "/cases/getcases/",
  CREATE_PROCESS_URL: "/workflow/createprocess/",
  GET_SERVICE_PROCESS_URL: "/workflow/getserviceprocesses/",
  MAKE_CATEGORY_LIVE: "/workflow/makecategorylive/",
  GET_ALL_DOC_TYPES: "/documents/getalldoctypes/",
  GET_SERVICE_DOC_TYPES: "/documents/getservicedoctypes/",
  GET_ALL_ANS_DOC_TYPES: "/documents/getallanswerdoctypes/",
  CREATE_CATEGORY: "/workflow/createcategory/",
  BULK_CREATE_CATEGORY: "/workflow/bulkcreatecategory/",
  GET_CATEGORIES: "/workflow/getcategories/",
  UPDATE_CATEGORY_TREE: "/workflow/updatecategorytree/",
  GET_CATEGORY_TREE_LIST: "/workflow/getcategorytreelist/",
  GET_BUSINESS_TEAM: "/users/getbusinessteam/",
  CREATE_DOC_TYPE: "/documents/createdoctype/",
  BULK_CREATE_DOC_TYPE: "/documents/bulkcreatedoctype/",
  BULK_CREATE_PROCESS: "/workflow/bulkcreateprocess/",
  GET_PROCESSES: "/workflow/getprocesses/",
  ADD_QUESTION_LINKS: "/questions/addquestionlinks/",
  BULK_CREATE_QUESTION_LINKS: "/questions/bulkcreatequestionlinks/",
  GET_ALL_QUESTION_LINKS: "/questions/getallquestionlinks/",
  GET_ALL_QUESTIONS: "/questions/getallquestions/",
  ADD_QUESTION: "/questions/addquestion/",
  ADD_QUESTION_OPTIONS: "/questions/addquestionoptions/",
  BULK_CREATE_QUESTIONS: "/questions/bulkcreatequestions/",
  GET_ALL_STATES: "/workflow/getallstates/",
  CREATE_STATES: "/workflow/createstates/",
  BULK_CREATE_STATES: "/workflow/bulkcreatestates/",
  CREATE_STEP: "/workflow/createstep/",
  BULK_CREATE_STEP: "/workflow/bulkcreatestep/",
  GET_ALL_STEPS: "/workflow/getallsteps/",
  GET_ALL_STEP_LINKS: "/workflow/getallsteplinks/",
  CREATE_STEP_LINKS: "/workflow/createsteplinks/",
  BULK_CREATE_STEP_LINKS: "/workflow/bulkcreatesteplinks/",
  GET_ALL_STEP_STATES: "/workflow/getallstepstates/",
  CREATE_STEP_STATE: "/workflow/createstepstate/",
  BULK_CREATE_STEP_STATES: "/workflow/bulkcreatestepstates/",
  CREATE_TAG: "/tags/createtag/",
  BULK_CREATE_TAG: "/tags/bulkcreatetags/",
  GET_BUSINESS_TAGS: "/tags/getbusinesstags/",
  GET_TODO_LIST_FOR_BUSINESS: "/todo/gettodolistsforbusiness/",
  CREATE_TODO: "/todo/createtodo/",
  CREATE_TODO_LIST: "/todo/createtodolist/",
  CREATE_STEP_TODO_LINK: "/todo/createsteptodolink/",
  BULK_CREATE_QUESTION_FORMS: "/questions/bulkcreatequestionforms/",
  BULK_CREATE_TODO_LISTS: "/todo/bulkcreatetodolists/",
  GET_STEP_TODO_LISTS_FOR_BUSINESS: "/todo/getsteptodolistsforbusiness/",
  GET_BUSINESS_DETAIL: "/users/getbusinessdetail/",
  CREATE_USER: "/users/createuser/",
  SEND_USER_INVITE: "/users/sendinvite/",
  DELETE_INVITED_USER: "/users/deleteinviteduser/",
  VALIDATE_INVITE: "/users/validateinvite/",
  COMPLETE_SET_PASSWORD: "/users/completesetpassword/",
  CHECK_AADHAR: "/users/checkaadhar/",
  CREATE_ROLE: "/users/createrole/",
  GET_STATES: "/workflow/getstates/",
  CHECK_SESSION_VALIDITY: "/users/checksessionvalidity/",
  GET_BUSINESSES: "/users/getbusinesses/",
  CREATE_BUSINESS: "/users/createbusiness/",
  CREATE_CLIENT: "/users/createclient/",
  GET_USER_ADDRESSES: "/users/getuseraddresses/",
  GET_CLIENT_DETAILS: "/users/getclientdetails/",
  CREATE_ADDRESS: "/users/createaddress/",
  UPDATE_CLIENT_CONTACT: "/users/updateclientcontact/",
  GET_USER_PHONE_NUMBERS: "/users/getuserphonenumbers/",
  CREATE_PHONE_NUMBER: "/users/createphonenumber/",
  GET_USER_ID_PROOFS: "/users/getuseridproofs/",
  UPLOAD_ID_PROOF: "/users/uploadidproof/",
  CHANGE_PASSWORD: "/users/changepassword/",
  UPLOAD_PROFILE_PIC: "/users/uploadprofilepic/",
  GET_SEARCH_LOG: "/cases/getsearchlog/",
  SEARCH_CASES: "/cases/searchcases/",
  LOGIN: "/trpauth/login/",
  CREATE_SESSION: "/users/createsession/",
  REFRESH_SESSION_TOKEN: "/users/refreshsessiontoken/",
  LOGOUT: "/users/logout/",
  GET_USER_DETAILS: "/users/getuserdetails/",
  GET_TEAM_MEMBER_DETAIL: "/users/getteammemberdetail/",
  GET_TEAM_MEMBER_PHONE_NUMBER: "/users/getteammemberphonenumber/",
  GET_TEAM_MEMBER_PROOFS: "/users/getteammemberproofs/",
  GET_TEAM_MEMBER_ADDRESS: "/users/getteammemberaddress/",
  GET_USER_ALERTS: "/alerts/getuseralerts/",
  GET_CASE_ACTIVITY: "/cases/getcaseactivity/",
  GET_CASE_UPLOADED_DOCUMENTS: "/cases/getcaseuploadeddocuments/",
  UPLOAD_USER_DOCUMENT: "/documents/uploaduserdocument/",
  BULK_UPLOAD_USER_DOCUMENT: "/documents/bulkuploaduserdocument/",
  DOWNLOAD_CASE_DOCUMENT_ZIP: "/documents/downloadcasedocumentzip/",
  EDIT_CASE_DOCUMENT: "/cases/editcasedocument/",
  MEDIA: "/media/",
  GET_CASE_MESSAGES: "/cases/getcasemessages/",
  UPDATE_CASE_MESSAGE: "/cases/updatecasemessage/",
  GET_CASE_STEPS: "/cases/getcasesteps/",
  GET_CASE_STEP_TODO_LIST: "/cases/getcasesteptodolist/",
  GET_CASE_STEP_STATES: "/cases/getcasestepstates/",
  CREATE_USER_TODO: "/todo/createusertodo/",
  UPDATE_CASE_STEP_TODOLIST_ITEM: "/cases/updatecasesteptodolistitem/",
  UPDATE_CASE_STEP_STATE: "/cases/updatecasestepstate/",
  GET_STEP_TODO_LIST: "/todo/getsteptodolist/",
  UPDATE_CASE_STEP_DETAILS: "/cases/updatecasestepdetails/",
  GET_CASE_NOTES: "/cases/getcasenotes/",
  UPDATE_CASE_NOTE: "/cases/updatecasenote/",
  GET_CASE_DETAILS: "/cases/getcasedetails/",
  GET_CASE_TAGS: "/cases/getcasetags/",
  GET_CLIENTS: "/users/getallclients/",
  GET_CLIENTS_LIST: "/users/getclients/",
  UPDATE_CASE: "/cases/updatecase/",
  UPDATE_CASE_TAGS: "/cases/updatecasetags/",
  SWITCH_CASE_STEP: "/cases/switchcasestep/",
  GET_CASE_ASSIGNMENT: "/cases/getcaseassignment/",
  ASSIGN_CASE: "/cases/assigncase/",
  SWITCH_PROCESS: "/cases/switchprocess/",
  UPDATE_CASE_STATUS: "/cases/updatecasestatus/",
  GET_ALL_CATEGORY_INFO: "/workflow/getallcategoryinfo/",
  CREATE_ALL_CATEGORIES: "/workflow/createallcategories/",
  INVITE_CLIENT: "/users/inviteclient/",
  DELETE_CLIENT: "/users/deleteclient/",
  SEARCH_CLIENTS: "/users/searchclients/",
  GET_CLIENT_CONTACTS: "/users/getclientcontacts/",
  GET_SUB_CATEGORY_FOR_CATEGORY: "/workflow/getsubcategoryforcategory/",
  GET_CASE_QUESTIONS: "/cases/getcasequestions/",
  GET_CASE_DOCUMENTS: "/cases/getcasedocuments/",
  INITIATE_CASE_DOC_ZIP: "/cases/initiatecasedoczip/",
  GET_CASE_DOC_ZIP: "/cases/getcasedoczip/",
  DOWNLOAD_CASE_DOC_ZIP: "/cases/downloadcasedoczip/",
  GET_EXPORT_JOBS: "/cases/getexportjobs/",
  DOWNLOAD_CLIENTS_EXPORT: "/users/downloadclientsexport/",
  INITIATE_CLIENT_ID_PROOF_ZIP: "/users/initiateclientidproofzip/",
  GET_CLIENT_ID_PROOF_ZIP: "/users/getclientidproofzip/",
  DOWNLOAD_CLIENT_ID_PROOF_ZIP: "/users/downloadclientidproofzip/",
  DOWNLOAD_CLIENT_DOCUMENT_ZIP: "/users/downloadclientdocumentzip/",
  ADD_UPDATE_CASE_ANSWERS: "/cases/addupdatecaseanswers/",
  CREATE_CASE: "/cases/createcase/",
  GET_CLIENT_GROUPS: "/users/getclientgroups/",
  GET_BUSINESS_CLIENT_GROUPS: "/users/getbusinessclientgroups/",
  CHECK_CLIENT_GROUP_SHORTCODE: "/users/checkclientgrp/",
  CREATE_CLIENT_GROUP: "/users/createclientgroup/",
  GET_GROUP_CLIENTS: "/users/getgroupclients/",
  GET_PROCESS_STEPS: "/workflow/getprocesssteps/",
  GET_QUESTIONS_FOR_CATEGORY: "/questions/getquestionsforcategory/",
  GET_PROCESS_TODO_LISTS: "/workflow/getprocesstodolists/",
  VERIFY_FORGOT_PASSWORD: "/users/verifyforgotpassword/",
  FORGOT_PASSWORD: "/users/forgotpassword/",
  RESET_PASSWORD: "/users/resetpassword/",
  GET_NOTICES: "/users/getnotices/",
  CREATE_NOTICE: "/users/createnotice/",
  GET_PROCESS_DETAILS: "/workflow/getprocessdetail/",
  CREATE_PROCESS_STEPS_AND_TASKS: "/workflow/createprocessstepsandtasks/",
  MAKE_PROCESS_LIVE: "/workflow/makeprocesslive/",
  DELETE_PROCESS: "/workflow/deleteprocess/",
  GET_CASE_STATS: "/cases/getcasestats/",
  VERIFY_CLIENT_SIGNUP: "/users/verifyclientsignup/",
  VERIFY_CLIENT_EMAIL: "/users/verifyclientemail/",
  RESEND_CLIENT_EMAIL: "/users/resendclientemail/",
  VERIFY_TRP_INVITE: "/users/verifytrpinvite/",
  RESEND_TRP_INVITE: "/users/resendtrpinvite/",
  SIGN_UP_CLIENT: "/users/signupclient/",
  SHORTCODE_VALIDATION: "/workflow/shortcodevalidation/",
  GET_BUSINESS_ROLES: "/users/getbusinessroles/",
  CHECK_BIZ_ROLE_SHORTCODE: "/users/checkbizrole/",
  CREATE_BUSINESS_ROLE: "/users/createbusinessrole/",
  GET_ROLE_PERMISSIONS: "/permission/getrolepermissions/",
  GET_ALL_ACTIONS: "/permission/getallactions/",
  SET_ROLE_PERMISSIONS: "/permission/setallrolepermissions/",
  GET_UPLOAD_INFO: "/users/getuploadinfo/",
  CLIENT_ZIP_FILES: "/users/clientzipfiles/",
  GET_CLIENT_ZIP_FILES: "/users/getclientzipfiles/",
  INVITE_CLIENT_EXTRACTED: "/users/inviteclientextracted/",
  BULK_INVITE_CLIENT_EXTRACTED: "/users/bulkinviteclientextracted/",
  GET_USERS_PERMISSION: "/permission/getuserpermissions/",
  CREATE_USER_PERMISSION: "/permission/createuserpermission/",
  GET_ROLE_PERMISSION: "/permission/getrolepermissions/",
  UPDATE_GROUP_CLIENTS: "/users/updategroupclients/",
  GET_CLIENT_CASES: "/cases/getclientcases/",
  UPDATE_CLIENT_GROUPS: "/users/updateclientgroups/",
  GET_USER_CREATED_NOTICES: "/users/getusercreatednotices/",
  GET_BUSINESS_NOTICES: "/users/getbusinessnotices/",
  GET_TICKETS: "/users/gettickets/",
  GET_SINGLE_TICKET_DETAILS: "/users/getsingleticket/",
  CREATE_TICKET: "/users/createticket/",
  CREATE_COMMUNICATION: "/users/createcommunication/",
  UPDATE_TICKET_STATUS: "/users/updateticketstatus/",
  UPDATE_TICKET_PRIORITY: "/users/updateticketpriority/",
  CASE_USER_DOCUMENT: "/cases/caseuserdocument/",
  GET_ACCOUNT_STATUS: "/users/getaccountstatus/",
  GENERATE_OTP: "/users/generateotp/",
  VERIFY_OTP: "/users/verifyotp/",
  ADD_BUSINESS_SECURITY: "/users/addbusinesssecurity/",
  GET_BUSINESS_SECURITY: "/users/getbusinesssecurity/",
  GET_ALL_BUSINESS_TAGS: "/tags/getallbusinesstags/",
  GET_ALL_CLIENTS: "/users/getallclients/",
  GET_DOWNLOAD_INFO: "/users/getdownloadinfo/",
  GET_ALL_TERMS: "/users/getallterms/",
  CREATE_TERMS: "/users/createterms/",
  Edit_TERMS: "/users/editterms/",
  GET_ACCEPTANCE_HISTORY: "/users/getacceptancehistory/",
  GET_SINGLE_TERMS: "/users/getsingleterms/",
  GET_TERMS_STATUS_CHECK: "/users/gettermsstatuscheck/",
  DELETE_TERMS: "/users/deleteterms/",
  CREATE_ACCEPTANCE_TERMS: "/users/createacceptanceterms/",
  DELETE_TAGS: "/tags/deletetag/",
  GET_DASHBOARD: "/reports/getdashboard/",
  GETDASHBOARD: "/reports/getdashboard/",
  DELETE_PROOF_DOCUMENT: "/users/deleteproofdocument/",
  DELETE_CASE_DOCUMENT: "/documents/deletecasedocument/",
  GET_GROUP_CLIENTS_NEW: "/users/getgroupclientsnew/",
  UPDATE_GROUP_CLIENTS_NEW: "/users/updategroupclientsnew/",
  GET_CLIENT_GROUPS_NEW: "/users/getclientgroupsnew/",
  UPDATE_CLIENT_GROUPS_NEW: "/users/updateclientgroupsnew/",
  BULK_CASE_CREATION: "/cases/bulkcasecreation/",
  DELETE_CASE: "/cases/deletecase/",
  FORCE_LOGOUT_USER: "/users/forcelogout/",
  GET_ALL_BUSINESS_NOTES: "/users/getallbiznotes/",
  GET_BUSINESS_NOTES: "/users/getbiznotes/",
  CREATE_BUSINESS_NOTES: "/users/createbiznotes/",
  EDIT_BUSINESS_NOTES: "/users/editbiznotes/",
  DELETE_BUSINESS_NOTES: "/users/deletebiznotes/",
  UPDATE_TICKET_SEVERITY: "/users/ticketseverityupdate/",
  GET_CONVERSATION_ID: "/chat/getconversationid/",
  GET_MESSAGES: "/chat/getmessages/",
  CREATE_MESSAGES: "/chat/createmessages/",
  GET_CONVERSATION_USERS: "/chat/getconversationuser/",
  CHAT_SEARCH: "/chat/chatsearch/",
  GET_LOGGEDINCLIENTS: "/reports/getloggedinclients/",
  GET_CASE_CREATION_STATUS: "/reports/casecreationstatus/",
  GET_USER_PROCESS: "/reports/getcaseuserprocess/",
  GET_TAGS_REPORT: "/reports/gettagcasesreport/",
  GET_PROOF_DOC_AUDIT_REPORT: "/reports/getproofdocauditreport/",
  GET_DOC_AUDIT_REPORT: "/reports/getdocauditreport/",
  GET_CASE_AUDIT_REPORT: "/reports/getcaseauditreport/",
  GET_USER_BUSINESS_DETAILS: "/users/getuserbusinessdetails/",
  CHECK_CASE_GROUP_SHORTCODE: "/cases/checkcasegrp/",
  CREATE_CASE_GROUP: "/cases/createcasegroup/",
  GET_ALL_CASE_GROUP: "/cases/getallcasegroup/",
  GET_CASE_GROUP_DETAILS: "/cases/getcasegroup/",
  GET_ALL_CASE_GROUP_ENTRY: "/cases/getallcasegroupentry/",
  EDIT_CASE_GROUP: "/cases/editcasegroup/",
  DELETE_CASE_GROUP: "/cases/deletecasegroup/",
  CREATE_CASE_GROUP_ENTRY: "/cases/createcasegroupentry/",
  ASSIGN_CASE_GROUP_ENTRY: "/cases/creategroupcaseentry/",
  GET_CASE_GROUP_ENTRY: "/cases/getallcasegroupentry/",
  GET_GROUP_CASE_ENTRY: "/cases/getgroupcaseentry/",
  UPDATE_ASSIGN_CASE_GROUP_ENTRY: "/cases/editgroupcaseentry/",
  DELETE_CASE_GROUP_ENTRY: "/cases/deletecasegroupentry/",
  CASE_GROUP_ASSIGN: "/cases/casegroupassign/",
  GET_NOTIFICATION: "/users/getbanners/",
  CREATE_NOTIFICATION: "/users/maintainbanners/",
  CREATE_CASE_GROUP_NOTE: "/cases/creatcasegroupnote/",
  DELETE_CHAT: "/chat/deletechat/",
  GET_PLATFORM_CATEGORIES: "/platform/getplatformcategories/",
  GET_CATEGORY_TEMPLATES: "/platform/getallplatformcategoryinfo/",
  GET_ALL_PLATFORM_CATEGORY_INFO: "/platform/getallplatformcategoryinfo/",
  CREATE_ALL_PLATFORM_CATEGORIES: "/platform/createallplatformcategories/",
  SHORTCODE_PLATFORM_VALIDATION: "/platform/shortcodeplatformvalidation/",
  GET_PLATFORM_PROCESSES: "/platform/getplatformprocesses/",
  GET_PLATFORM_SERVICE_PROCESSES: "/platform/getplatformserviceprocesses/",
  GET_SUBCATEGORY_FOR_PLATFORM_CATEGORY: "/platform/getsubcategoryforplatformcategory/",
  GET_PLATFORM_PROCESS_DETAILS: "/platform/getplatformprocessdetail/",
  GET_PLATFORM_STEPS: "/platform/getplatformsteps/",
  GET_PLATFORM_PROCESS_STEPS: "/platform/getplatformprocesssteps/",
  CREATE_PLATFORM_PROCESSES: "/platform/createplatformprocess/",
  EXTRACT_TEMPLATE: "/platform/extracttemplate/",
  APPLY_TEMPLATE: "/platform/applytemplate/",
  CREATE_PLATFORM_PROCESS_STEPS_AND_TASK: "/platform/createplatformprocessstepsandtasks/",
  GET_PLATFORM_PROCESS_TODOLISTS: "/platform/getplatformprocesstodolists/",
  GET_PLATFORM_QUESTION: "/platform/getplatformquestions/",
  GET_PLATFORM_QUESTIONS_FOR_CATEGORY: "/platform/getplatformquestionsforcategory/",
  CREATE_PLATFORM_QUESTION: "/platform/createplatformquestion/",
  GET_PLATFORM_DOC_TYPES: "/platform/getplatformdoctypes/",
  CREATE_PLATFORM_DOC_TYPES: "/platform/createplatformdoctype/",
  DELETE_PLATFORM_DOC_TYPES: "/platform/deleteplatformdoctypes/",
  CREATE_PLATFORM_QUESTION_OPTION: "/platform/createplatformquestionoption/",
  MAKE_PLATFORM_CATEGORY_LIVE: "/platform/makeplatformcategorylive/",
  MAKE_PLATFORM_PROCESS_LIVE: "/platform/makeplatformprocesslive/",
  APPLY_TEMPLATE_NEW: "/platform/applytemplatenew/",
} as const;

type ApiPathKey = keyof typeof API_PATHS;

function buildApiUrls(): Record<ApiPathKey, string> {
  const base = getApiBaseUrl();
  const out = {} as Record<ApiPathKey, string>;
  for (const key of Object.keys(API_PATHS) as ApiPathKey[]) {
    out[key] = base ? `${base}${API_PATHS[key]}` : "";
  }
  return out;
}

let _apiUrls: Record<ApiPathKey, string> | null = null;

function getApiUrls(): Record<ApiPathKey, string> {
  if (_apiUrls === null) _apiUrls = buildApiUrls();
  return _apiUrls;
}

const apiBase = {
  get baseUrl(): string {
    return getApiBaseUrl();
  },
  get socketUrl(): string {
    return getSocketUrl();
  },
  path: apiPath,
  get urls(): Record<ApiPathKey, string> {
    return getApiUrls();
  },
};

export type ApiType = {
  readonly baseUrl: string;
  readonly socketUrl: string;
  path: (_path: string) => string;
  readonly urls: Record<ApiPathKey, string>;
} & Record<ApiPathKey, string>;

export const api: ApiType = new Proxy(apiBase, {
  get(target, prop: string) {
    if (prop in target) return (target as Record<string, unknown>)[prop];
    const paths = API_PATHS as Record<string, string>;
    if (prop in paths) return apiPath(paths[prop]);
    return undefined;
  },
}) as ApiType;

export default api;
